import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { USER_TYPES } from '@medflex/utils/constants';
import ScrollableTabs from '@medflex/components/stories/ScrollableTabs';
import { Route, Switch, useHistory, useRouteMatch, useLocation } from 'react-router-dom';
import Badge, { KINDS, SIZES } from '@medflex/components/stories/Badge';
import { FEATURE_KEYS } from '@medflex/utils/subscriptionEnums';
import PhysicianConfigModule from './PhysicianConfigModule';
import PhysicianPagesContactModule from './PhysicianPagesContactModule';
import InfoMaterialForPatientsModule from './InfoMaterialForPatientsModule';
import PhysicianStatusModule from './PhysicianStatusModule';
import TextSnippetsModule from './TextSnippetsModule';
import useUserProfile from '../../hooks/useUserProfile';
import PhysicianSubscriptionModule from './PhysicianSubscriptionModule';
import ChatSettingsModule from './ChatSettingsModule';
import InquiryManagementModule from './InquiryManagement/InquiryManagementModule';
import PVSIntegrationModule from './PVSIntegration/PVSIntegrationModule';
import UserTagsModule from './UserTagsModule';
import useFeatures from '../../hooks/useFeatures';
import useSubscription from '../../hooks/useSubscription';

const PhysicianAdministrationModule = () => {
  const { t } = useTranslation('physician');
  const history = useHistory();
  const location = useLocation();
  const { path } = useRouteMatch();
  const { userProfile } = useUserProfile();
  const [pamFeature] = useFeatures([FEATURE_KEYS.PAM]);
  const subscripton = useSubscription();

  const tabs = useMemo(() => {
    const physicianTabs = [];

    if (userProfile.type === USER_TYPES.PHYSICIAN) {
      physicianTabs.push(
        {
          key: 'profile',
          label: t('PhysicianAdministrationModule.tabs.profile'),
        },
        ...(!subscripton.organizationId
          ? [
              {
                key: 'subscription',
                label: t('PhysicianAdministrationModule.tabs.subscription'),
              },
            ]
          : []),
        { key: 'chat', label: t('PhysicianAdministrationModule.tabs.chat') },
        ...(pamFeature.active
          ? [
              {
                key: 'tags',
                label: t('PhysicianAdministrationModule.tabs.tags'),
              },
            ]
          : []),
      );
    }

    return [
      ...physicianTabs,
      { key: 'contact', label: t('PhysicianAdministrationModule.tabs.contact') },
      {
        key: 'inquiryManagement',
        label: t('PhysicianAdministrationModule.tabs.inquiryManagement'),
      },
      { key: 'status', label: t('PhysicianAdministrationModule.tabs.status') },
      { key: 'snippets', label: t('PhysicianAdministrationModule.tabs.snippets') },
      {
        key: 'infoMaterialForPatients',
        label: t('PhysicianAdministrationModule.tabs.infoMaterialForPatients'),
      },
      ...(window.electron?.gdt
        ? [
            {
              key: 'pvs-integration',
              label: t('PhysicianAdministrationModule.tabs.pvsIntegration'),
              additionalChild: (
                <Badge kind={KINDS.SIGNAL} size={SIZES.SMALL} className="ml-2">
                  {t('PhysicianAdministrationModule.tabs.beta')}
                </Badge>
              ),
            },
          ]
        : []),
    ];
  }, [pamFeature.active, subscripton.organizationId, t, userProfile.type]);

  const onTabClick = tab => {
    history.push(`${path}/${tab}`);
  };

  return (
    <div
      data-component="PhysicianAdministrationModule"
      className="flex flex-1 flex-col overflow-y-auto"
    >
      <h1 className="m-4 text-2xl font-semibold">{t('configuration.title')}</h1>
      <div className="flex h-12 flex-shrink-0 bg-white">
        <ScrollableTabs
          active={location.pathname.split('/')[2]}
          onClick={tab => onTabClick(tab)}
          tabs={tabs}
          scrollable
        />
      </div>
      <div className="max-w-3xl p-4 lg:p-8">
        <Switch>
          {userProfile.type === USER_TYPES.PHYSICIAN && [
            <Route key="Administration_profile" exact path={`${path}/profile`}>
              <PhysicianConfigModule />
            </Route>,
            !subscripton.organizationId && (
              <Route key="Administration_subscription" exact path={`${path}/subscription`}>
                <PhysicianSubscriptionModule />
              </Route>
            ),
            <Route key="Administration_chat" exact path={`${path}/chat`}>
              <ChatSettingsModule />
            </Route>,
            pamFeature.active && (
              <Route exact path={`${path}/tags`}>
                <UserTagsModule />
              </Route>
            ),
          ]}
          <Route exact path={`${path}/contact`}>
            <PhysicianPagesContactModule />
          </Route>
          <Route path={`${path}/inquiryManagement`}>
            <InquiryManagementModule />
          </Route>
          <Route exact path={`${path}/infoMaterialForPatients`}>
            <InfoMaterialForPatientsModule />
          </Route>
          <Route exact path={`${path}/status`}>
            <PhysicianStatusModule />
          </Route>
          <Route exact path={`${path}/snippets`}>
            <TextSnippetsModule />
          </Route>
          {window.electron?.gdt && (
            <Route exact path={`${path}/pvs-integration`}>
              <PVSIntegrationModule />
            </Route>
          )}
        </Switch>
      </div>
    </div>
  );
};

export default PhysicianAdministrationModule;
